import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import useTabStore, {
  loadUserTabs,
  retrySaveTabs,
} from "../stores/useTabStore";
import TabSystem from "../layout-components/TabSystem";
import DemoBanner from "@/ui-components/DemoBanner";
import { getWidgetById } from "@/utils/widgets";
import { trackFeatureUsage } from "@/utils/gtm";

let sessionOpenTracked = false;

const DashboardBuilder = () => {
  const { authenticated } = useAuth();
  const [, setSearchParams] = useSearchParams();
  const activeTabId = useTabStore((state) => state.activeTabId);
  const tabsLoaded = useTabStore((state) => state.tabsLoaded);
  const tabsLoadFailed = useTabStore((state) => state.tabsLoadFailed);
  const tabsFromCache = useTabStore((state) => state.tabsFromCache);
  const tabsSaveFailed = useTabStore((state) => state.tabsSaveFailed);
  const initialTabApplied = useRef(false);
  const [retrying, setRetrying] = useState(false);
  const [resaving, setResaving] = useState(false);

  // Unauth users have tabs immediately; auth users must wait for server
  // response, or for a cached layout to stand in when that read fails.
  const tabsReady = !authenticated || tabsLoaded || tabsFromCache;

  // Once tabs are ready, apply ?tab= from URL if valid (one-time)
  useEffect(() => {
    if (!tabsReady || initialTabApplied.current) return;
    initialTabApplied.current = true;

    const tabParam = new URLSearchParams(window.location.search).get("tab");
    if (!tabParam) return;

    const { tabs, activeTabId, switchTab } = useTabStore.getState();
    if (tabParam !== activeTabId && tabs.some((t) => t.id === tabParam)) {
      switchTab(tabParam);
    }
  }, [tabsReady]);

  // When activeTabId changes, sync it to the URL
  useEffect(() => {
    if (!tabsReady) return;

    setSearchParams({ tab: activeTabId }, { replace: true });
  }, [activeTabId, tabsReady, setSearchParams]);

  // Fire one GA4 event per open widget on initial dashboard load
  useEffect(() => {
    if (!tabsReady || sessionOpenTracked) return;
    sessionOpenTracked = true;

    const { tabs, activeTabId } = useTabStore.getState();
    const activeTab = tabs.find((t) => t.id === activeTabId);
    if (!activeTab) return;

    for (const compositeId of activeTab.enabledWidgets) {
      const [widgetId] = compositeId.split("-");
      const widget = getWidgetById(widgetId);
      if (!widget) continue;
      trackFeatureUsage({
        feature_name: "widget_interaction",
        feature_action: "session_open",
        feature_detail: widget.name,
      });
    }
  }, [tabsReady]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await loadUserTabs();
    } finally {
      setRetrying(false);
    }
  };

  const handleResave = async () => {
    setResaving(true);
    try {
      await retrySaveTabs();
    } finally {
      setResaving(false);
    }
  };

  // The layout read failed and there's no cached copy to fall back on. The
  // user's saved tabs are untouched on the server — the store deliberately
  // stays unloaded so nothing overwrites them — so show the failure rather
  // than defaults that would look like the layout was wiped.
  if (!tabsReady && tabsLoadFailed) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 px-6 text-center text-gray-300">
        <p className="font-medium">Couldn&apos;t load your dashboard layout</p>
        <p className="max-w-md text-sm text-gray-400">
          We couldn&apos;t reach the server. Your saved tabs and filters are
          safe and haven&apos;t been changed.
        </p>
        <button
          type="button"
          onClick={handleRetry}
          disabled={retrying}
          className="mt-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {retrying ? "Retrying..." : "Try again"}
        </button>
      </div>
    );
  }

  if (!tabsReady) {
    return (
      <div className="animate-pulse flex h-screen items-center justify-center text-center text-gray-300">
        Loading dashboard...
      </div>
    );
  }

  return (
    <>
      {!authenticated && <DemoBanner />}
      {/* Only when the layout did load — the cached-session banner below
          already says changes aren't being saved. */}
      {tabsSaveFailed && !tabsFromCache && (
        <div className="flex items-center justify-center gap-4 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-center">
          <span className="text-xs text-amber-200">
            Your latest layout changes haven&apos;t been saved — we&apos;ll try
            again with your next change.
          </span>
          <button
            type="button"
            onClick={handleResave}
            disabled={resaving}
            className="rounded-md bg-gray-700/40 px-3 py-1 text-xs font-medium text-gray-200 transition-colors hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {resaving ? "Saving..." : "Save now"}
          </button>
        </div>
      )}
      {tabsFromCache && (
        <div className="flex items-center justify-center gap-4 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-center">
          <span className="text-xs text-amber-200">
            Showing your last saved layout — we couldn&apos;t reach the server,
            so changes made now won&apos;t be saved.
          </span>
          <button
            type="button"
            onClick={handleRetry}
            disabled={retrying}
            className="rounded-md bg-gray-700/40 px-3 py-1 text-xs font-medium text-gray-200 transition-colors hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {retrying ? "Reconnecting..." : "Reconnect"}
          </button>
        </div>
      )}
      <TabSystem />
    </>
  );
};

export default DashboardBuilder;
